import React, {useEffect, useState} from "react";
import { motion, AnimatePresence } from "framer-motion";

import {SearchResultsContainer} from "../../ProductSearch";
import {
    suggest,
    resolveEmailSha256,
    resolveRecoUid,
    getRecommendationProducts,
    getStoreCurrency
} from "../../common/services";
import PersonalizedPicksCarousel from './PersonalizedPicksCarousel';
import MultiItemCarousel from '../../common/components/MultiItemCarousel';
import RecoProductsGrid from './RecoProductsGrid';
import {ConversationHistoryPanel} from "./index";

const SearchResultsContent = ({
                                  contentRef,
                                  hasScrolled,
                                  setHasScrolled,
                                  loading,
                                  is_data_load,
                                  setLoading,
                                  setIsDataLoad,
                                  searchQuery,
                                  currentText,
                                  currentHold,
                                  apiConfig,
                                  onResultsReady,
                                  historySelection,
                                  isHistoryVisible=false,
                                  conversationHistory,
                                  onAssistantMessageClick,
                                  onToggleHistory
                              }) => {

    const [recoProducts, setRecoProducts] = useState({items: [], loading: false, error: null});
    const [currencyCode, setCurrencyCode] = useState(null);
    const [roundingEnabled, setRoundingEnabled] = useState(null);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        const check = () => setIsDesktop(window.innerWidth >= 1024);
        check();

        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    const effectiveQuery = typeof searchQuery === 'string' && searchQuery.trim()
        ? searchQuery.trim()
        : (typeof currentText === 'string' ? currentText.trim() : '');

    /* ================= SCROLL ================= */
    useEffect(() => {
        const handleScroll = () => {
            if (contentRef.current) {
                const {scrollTop, scrollHeight, clientHeight} = contentRef.current;
                const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;
                if (scrollPercentage >= 95 && !hasScrolled) {
                    setHasScrolled(true);
                }
            }
        };

        const el = contentRef.current;
        el?.addEventListener("scroll", handleScroll);
        return () => el?.removeEventListener("scroll", handleScroll);
    }, [hasScrolled, contentRef, setHasScrolled]);

    /* ================= RECOMMENDATIONS ================= */
    useEffect(() => {
        let mounted = true;

        async function loadRecommendations() {
            try {
                setRecoProducts(prev => ({...prev, loading: true, error: null}));

                const emailSha = resolveEmailSha256();
                const uid = resolveRecoUid();
                const data = await suggest(emailSha, uid);

                const recItems = Array.isArray(data?.recommendation_items) ? data.recommendation_items : [];
                const priceAnchor = data?.profile?.price_anchor ?? null;
                try {
                    if (typeof window !== 'undefined' && window.localStorage && data?.profile) {
                        window.localStorage.setItem('RECOMDO_PROFILE_CACHE', JSON.stringify(data.profile));
                        window.localStorage.setItem('RECOMDO_PROFILE_CACHE_TS', String(Date.now()));
                    }
                } catch (_) {
                }
                if (!recItems.length) {
                    if (mounted) setRecoProducts({items: [], loading: false, error: null});
                    return;
                }

                const products = await getRecommendationProducts(
                    recItems,
                    apiConfig.API_BASE_URL,
                    apiConfig.clientKey,
                    apiConfig.accessKey,
                    apiConfig.accessSecret,
                    priceAnchor
                );

                if (mounted) {
                    setRecoProducts({
                        items: Array.isArray(products) ? products : [],
                        loading: false,
                        error: null
                    });
                }

            } catch (e) {
                if (mounted) {
                    setRecoProducts({
                        items: [],
                        loading: false,
                        error: e?.message || 'Failed to load recommendations'
                    });
                }
            }
        }

        loadRecommendations();
        return () => { mounted = false; };
    }, []);

    /* ================= CURRENCY ================= */
    useEffect(() => {
        let mounted = true;

        const loadCurrency = async () => {
            const currency = await getStoreCurrency(
                apiConfig.API_BASE_URL,
                apiConfig.clientKey,
                apiConfig.accessKey,
                apiConfig.accessSecret
            );

            if (mounted) {
                setCurrencyCode(currency.currencyCode);
                setRoundingEnabled(currency.roundingEnabled);
            }
        };

        loadCurrency();
        return () => { mounted = false; };
    }, [apiConfig.API_BASE_URL, apiConfig.clientKey, apiConfig.accessKey, apiConfig.accessSecret]);

    return (
        <div
            ref={contentRef}
            className="flex-grow overflow-y-auto overflow-x-hidden bg-[#F9F9F9] pb-[60px] px-3 md:px-5 lg:px-6 xl:px-10 sm:pb-0"
        >

            {/* ================= TOP MOBILE RECO ================= */}
            {!recoProducts.loading && !recoProducts.error && recoProducts.items && recoProducts.items.length > 0 && (
                <div className={`${isHistoryVisible ? 'hidden' : 'block'} lg:hidden md:hidden pb-4`}>
                    {(is_data_load || loading) ? (
                        apiConfig.is_use_pick_slider ? (
                            <>
                                <div className="text-sm font-medium text-gray-800 mb-2 px-1">
                                    Personalised picks
                                </div>

                                <MultiItemCarousel
                                    orientation="horizontal"
                                    itemsPerGroup="5"
                                    showDots={true}
                                    dotSize='xs'
                                    autoAdvanceTime={0}
                                    items={recoProducts.items.map((p, idx) => ({
                                        id: p.id || idx,
                                        title: p.name,
                                        image: p.image,
                                        url: p.url || p.url_key,
                                        url_key: p.url_key,
                                    }))}
                                    query={effectiveQuery}
                                    clientKey={apiConfig.clientKey}
                                    showTitle={false}
                                />
                            </>
                        ) : null
                    ) : (
                        <RecoProductsGrid
                            products={recoProducts.items}
                            hasScrolled={hasScrolled}
                            setHasScrolled={setHasScrolled}
                            currentQuery={currentText}
                            clientKey={apiConfig.clientKey}
                            currencyCode={currencyCode}
                            roundingEnabled={roundingEnabled}
                        />
                    )}
                </div>
            )}

            {/* ================= MAIN LAYOUT ================= */}
            <div className={`flex gap-6 ${isHistoryVisible ? 'justify-center lg:mt-[5px]' : ''}`}>

                {/* LEFT COLUMN */}
                <div className={` ${isHistoryVisible ? 'order-2 lg:w-[30%] xl:w-[25%] absolute z-20 w-full lg:relative' : 'hidden md:block'}`}>

                    {(is_data_load || loading) && !isHistoryVisible ? (
                        <div className="sticky top-4 md:w-56 lg:w-60 xl:w-64 flex-shrink-0">
                            <PersonalizedPicksCarousel
                                products={recoProducts.items}
                                loading={recoProducts.loading}
                                error={recoProducts.error}
                                sectionTitle="Personalised picks"
                                clientKey={apiConfig.clientKey}
                                currentQuery={effectiveQuery}
                            />
                        </div>
                    ) : isHistoryVisible ? (

                        <AnimatePresence>
                            <motion.div
                                key="history"
                                initial={{ x: 80, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: 80, opacity: 0 }}
                                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                className="pointer-events-none sticky top-0"
                            >
                                <div className="px-3 md:px-0 pointer-events-auto">
                                    <ConversationHistoryPanel
                                        onToggleHistory={onToggleHistory}
                                        items={conversationHistory}
                                        onAssistantMessageClick={onAssistantMessageClick}
                                    />
                                </div>
                            </motion.div>
                        </AnimatePresence>

                    ) : (
                        <RecoProductsGrid
                            products={recoProducts.items}
                            hasScrolled={hasScrolled}
                            setHasScrolled={setHasScrolled}
                            currentQuery={currentText}
                            clientKey={apiConfig.clientKey}
                            currencyCode={currencyCode}
                            roundingEnabled={roundingEnabled}
                        />
                    )}
                </div>

                {/* RIGHT COLUMN */}
                <motion.div
                    className="min-w-0 flex-1"
                    animate={{
                        width: !isHistoryVisible && !is_data_load ? "0%" : "100%",
                        x: isHistoryVisible && isDesktop ? -20 : 0
                    }}
                    transition={{
                        width: { duration: 0.3, ease: "easeOut" },
                        x: { duration: 0.3, ease: "easeOut" }
                    }}
                >
                    <SearchResultsContainer
                        loading={loading}
                        isDataLoaded={is_data_load}
                        setLoading={setLoading}
                        setIsDataLoaded={setIsDataLoad}
                        hasScrolled={hasScrolled}
                        setHasScrolled={setHasScrolled}
                        searchQuery={searchQuery}
                        hold={currentHold}
                        apiBaseUrl={apiConfig.API_BASE_URL}
                        clientKey={apiConfig.clientKey}
                        accessKey={apiConfig.accessKey}
                        accessSecret={apiConfig.accessSecret}
                        currencyCode={currencyCode}
                        roundingEnabled={roundingEnabled}
                        onResultsReady={onResultsReady}
                        historySelection={historySelection}
                    />
                </motion.div>

            </div>
        </div>
    );
};

export default SearchResultsContent;